import { navigatorClient } from "@/api";
import { formatDate } from "@/lib/utils";
import type { ApiResponse } from "@/types";

import type { FinderCard } from "./finder-service";

// F07 · F09 My Page (backend /me). 북마크 · 비교함 담기/빼기는 finder-service의 setMark · listMarks를 같이 써요.

export type MySection = "profile" | "bookmarks" | "compare" | "inquiries" | "recent";

export interface MyProfile {
  name: string | null;
  email: string;
  company_name: string | null;
  department: string | null;
  position: string | null;
  phone: string | null;
  company_country: string | null;
  joined_at: string | null;
  login_method: string;
  user_type: string;
  user_type_label: string;
  status_label: string;
  can_withdraw: boolean;
  sections: MySection[];
}

export interface MySummary {
  profile: MyProfile;
  counts: { bookmarks: number; compare: number; inquiries: number; recent_views: number; assistant_sessions: number };
}

export async function getMySummary(signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<MySummary>>("/me/summary", { signal });
  return data.data!;
}

export async function updateMyProfile(body: { name: string; phone: string }) {
  const { data } = await navigatorClient.patch<ApiResponse<MyProfile>>("/me/profile", body);
  return data.data!;
}

export async function withdrawMe(body: { email: string; agreed: boolean }) {
  const { data } = await navigatorClient.post<ApiResponse<{ status: string }>>("/me/withdraw", body);
  return data.data!;
}

export type InquiryStatus = "new" | "in_review" | "resolved";

export interface MyInquiryItem {
  id: string;
  status: InquiryStatus;
  status_label: string;
  type_label: string;
  title: string;
  // 문의 내용 한 줄 미리보기 (Figma ver.7)
  preview?: string;
  target: string | null;
  created_at: string | null;
  replied_at: string | null;
}

export interface MyInquiryList {
  items: MyInquiryItem[];
  counts: { all: number; new: number; in_review: number; resolved: number };
}

export async function listMyInquiries(status?: InquiryStatus, signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<MyInquiryList>>("/me/inquiries", { params: status ? { status } : undefined, signal });
  return data.data!;
}

export interface MyInquiryDetail {
  id: string;
  status: InquiryStatus;
  status_label: string;
  type_label: string;
  title: string;
  steps: { label: string; at: string | null; done: boolean }[];
  content: string;
  created_at: string | null;
  attachments: {
    agent_id: string | null;
    agent_name: string | null;
    supplier_name: string | null;
    case_study_id: string | null;
    case_title: string | null;
    // 진입 출처 ("에이전트 상세" · "Case Study" …), GNB 진입은 null
    source_label?: string | null;
  };
  replies: { team: string; body: string; created_at: string | null }[];
  // 담당 팀 (정보 띠)
  team_name: string;
}

export async function getMyInquiry(id: string, signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<MyInquiryDetail>>(`/me/inquiries/${id}`, { signal });
  return data.data!;
}

export async function listRecentAgents(signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<{ items: (FinderCard & { viewed_at: string | null })[]; days: number; limit: number }>>(
    "/me/recent-agents",
    { signal },
  );
  return data.data!;
}

// 비교표 대표 검증 지표 한 건: 항목 / 도입 전 → 도입 후 · 변화율 (Figma ver.7 비교표 B안)
export interface CompareMetric {
  name: string;
  before: string | null;
  after: string | null;
  delta: string | null;
}

// 칸 값: 한 줄 글 · 줄마다 하나씩 목록(도입 형태 · 배포 · 가격) · 지표 목록. 볼 수 없는 에이전트는 null
export type CompareCell = string | string[] | CompareMetric[] | null;

export interface CompareList {
  items: (FinderCard & { added_at: string | null })[];
  count: number;
  limit: number;
  rows: { key: string; label: string; values: (string | null)[]; cells?: CompareCell[] }[];
}

export async function listCompare(signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<CompareList>>("/me/compare", { signal });
  return data.data!;
}

// 날짜만 (YYYY-MM-DD), 사용자 시간대 기준: 서버 시각은 UTC라 문자열을 자르면 한국 시간 00~09시가 전날로 보여요.
export function dateOnly(iso: string | null | undefined) {
  return iso ? formatDate(iso, "YYYY-MM-DD") : "-";
}

// "오늘 조회" · "n일 전 조회" (Figma 203:8100)
export function viewedLabel(iso: string | null | undefined, now = new Date()) {
  if (!iso) return "";
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(new Date(iso))) / 86_400_000);
  return diff <= 0 ? "오늘 조회" : `${diff}일 전 조회`;
}
